import type { ScrollAlignmentType } from '@unifyapps/defs/blocks/common/methodSchema/scrollIntoView';
import { isNative } from '@unifyapps/ui/utils/isNative';
import scrollIntoView from 'scroll-into-view-if-needed';
import { copyBlock } from '../../../utils/copyBlock';
import { ARENA_SELECTOR } from '../../const';
import type { BlockControlMethod } from '../../types/blockDefinition';
import { getBlockElementSelector, getTargetBlockElement } from '../../utils/block';

// Do not show these blocks in block picker (matched by component type, not display name)
export const BLOCKS_TO_HIDE: string[] = [
  'CommentList',
  'CommentInput',
  'HeadlessCopilot',
  'KnowledgeBuilder',
  'Stepper',
  'TemplateComponent',
  'SimpleAgentBuilder',
  'SkillEditor',
];

// blocks to hide only in mobile application builder (matched by component type)
export const BLOCKS_TO_HIDE_MOBILE: string[] = ['AxisIconPicker', 'Camera'];

export const BLOCKS_VISIBLE_ONLY_IN_TEMPLATE_COMPONENT: string[] = ['SlotOutlet'];

// delay before scrolling parent window to show iframe
// allows iframe's internal scroll animation to complete first
const IFRAME_PARENT_SCROLL_DELAY_MS = 100;

const EXPORT_BLOCK_METHOD: BlockControlMethod = {
  id: 'exportBlock' as const,
  label: 'Export Block',
  method: (block, _, __, helpers) => {
    const blockId = block?.id;
    const { onExportBlock } = helpers;

    if (blockId) {
      void onExportBlock?.(blockId);
    }

    return Promise.resolve();
  },
};

const SCROLL_INTO_VIEW_METHOD: BlockControlMethod = {
  id: 'scrollIntoView' as const,
  label: 'Scroll Into View',
  method: (block, action) => {
    try {
      const blockId = block?.id;
      const { methodPayload } = action.payload as {
        methodPayload: { alignment?: ScrollAlignmentType; smooth?: boolean };
      };
      const { alignment = 'nearest', smooth = true } = methodPayload || {};

      // handle native (not supported yet)
      if (isNative()) {
        console.warn('scrollIntoView not supported on native');
        return Promise.resolve();
      }

      if (!blockId) {
        console.warn('scrollIntoView: blockId not found');
        return Promise.resolve();
      }

      // try to get page element (may not always be present)
      const containerEl = document.querySelector('[data-interface-page-id]') as HTMLElement | null;
      const scrollContainer = containerEl?.closest(ARENA_SELECTOR) as HTMLElement | null;

      // find target block element - try with container first, then fallback to document
      let element = getTargetBlockElement({
        targetBlockId: blockId,
        pageEl: containerEl,
      });

      // if not found in container, try searching in document as fallback
      if (!element) {
        element = document.querySelector(getBlockElementSelector(blockId)) as HTMLElement | null;
      }

      if (!element) {
        console.warn(`scrollIntoView: block "${blockId}" not found in DOM`);
        return Promise.resolve();
      }

      // scroll into view
      scrollIntoView(element, {
        scrollMode: 'if-needed',
        block: alignment,
        inline: 'nearest',
        behavior: smooth ? 'smooth' : 'auto',
        boundary: scrollContainer || undefined,
      });

      // handle iframe: scroll parent window to show the iframe after inner scroll
      if (window.parent !== window) {
        setTimeout(() => {
          try {
            // try to get iframe element and use native scrollIntoView
            // this works because Element.scrollIntoView is a method on the Element itself
            const iframe = window.frameElement as HTMLIFrameElement | null;
            // first scroll the iframe to show it
            if (iframe) {
              iframe.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: alignment === 'nearest' ? 'center' : alignment,
                inline: 'center',
              });
            }

            // then scroll the element to show it
            element.scrollIntoView({
              behavior: smooth ? 'smooth' : 'auto',
              block: alignment === 'nearest' ? 'center' : alignment,
              inline: 'center',
            });
          } catch (error) {
            console.error('Failed to scroll parent window to show the iframe', error);
          }
        }, IFRAME_PARENT_SCROLL_DELAY_MS);
      }
    } catch (error) {
      console.error('scrollIntoView: error occurred', error);
    }

    return Promise.resolve();
  },
};

const COPY_BLOCK: BlockControlMethod = {
  id: 'copyBlock' as const,
  label: 'Copy Block',
  hiddenUnlessSelected: true,
  method: async (blockState, _action, _blockRef) => {
    await copyBlock(blockState?.id);
  },
};

export const COMMON_BLOCK_METHODS = [EXPORT_BLOCK_METHOD, SCROLL_INTO_VIEW_METHOD, COPY_BLOCK];
// TODO: currently export block is not supported in mobile
export const COMMON_BLOCK_METHODS_MOBILE = [];
